import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fabric } from 'fabric';
import { ProyectosService } from '../proyectos.service';
import { AuthService } from '../auth.service';
declare var jsPDF: any;

interface CanvasElement {
  id: string;
  type: 'text' | 'image' | 'shape';
  name: string;
  object: any;
}

@Component({
  selector: 'app-banner-editor',
  templateUrl: './banner-editor.component.html',
  styleUrls: ['./banner-editor.component.css']
})
export class BannerEditorComponent implements OnInit, OnDestroy, AfterViewInit {
  isDarkMode = true;
  private themeObserver?: MutationObserver;
  
  // Canvas y elementos
  private canvas: any = null;
  canvasElements: CanvasElement[] = [];
  
  // Getter para acceder al canvas desde el template si es necesario
  get canvasInstance() {
    return this.canvas;
  }
  
  // Configuración del canvas
  canvasWidth = 1000;
  canvasHeight = 400;
  backgroundColor = '#FFFFFF';
  
  // Plantillas predefinidas
  selectedTemplate: string = 'personalizado';
  templates = [
    { 
      id: 'personalizado', 
      name: 'Personalizado', 
      width: 0, 
      height: 0,
      ratio: null as any,
      description: ''
    },
    { 
      id: 'portada-web', 
      name: '2:1 (Portada web)', 
      width: 1920, 
      height: 960,
      ratio: 2/1,
      description: 'Portada web'
    },
    { 
      id: 'banner-rectangular', 
      name: '1.91:1 (Banner rectangular)', 
      width: 1200, 
      height: 628,
      ratio: 1.91/1,
      description: 'Banner rectangular'
    },
    { 
      id: 'flyer-cuadrado', 
      name: '1:1 (Flyer cuadrado)', 
      width: 1080, 
      height: 1080,
      ratio: 1/1,
      description: 'Flyer cuadrado'
    },
    { 
      id: 'flyer-rectangular', 
      name: '3:2 (Flyer rectangular)', 
      width: 1500, 
      height: 1000,
      ratio: 3/2,
      description: 'Flyer rectangular'
    }
  ];
  
  // Configuración de fondo (sólido o degradado)
  backgroundType: 'solid' | 'gradient' = 'solid';
  gradientColorStart = '#000000';
  gradientColorEnd = '#FF0000';
  gradientDirection: 'horizontal' | 'vertical' | 'diagonal' = 'horizontal';
  
  // Configuración de esquinas redondeadas
  roundedCorners = false;
  cornerRadius = 20;
  
  // Configuración de borde
  enableBorder = false;
  borderWidth = 5;
  borderColor = '#000000';
  
  // Configuración de exportación JPG
  jpgQuality = 92; // Calidad de compresión JPG (0-100)
  
  // Menú de exportación
  showExportMenu = false;
  showPngOptions = false;
  pngBackgroundType: 'color' | 'transparent' = 'color';
  
  // Dropdown de plantillas
  showTemplateDropdown = false;
  
  // Menú lateral estilo VS Code
  activePanel: 'config' | 'content' | 'elements' | 'properties' | null = null;
  
  // Copiar y pegar
  copiedObject: any = null;
  
  // Notificación de guardado
  showSaveNotification = false;
  saveNotificationTimeout: any = null;
  
  // Drag and drop para capas
  draggedElementIndex: number | null = null;
  dragOverIndex: number | null = null;
  
  // Panel de propiedades del elemento
  selectedElementForEdit: any = null;
  
  // Modal de guardar proyecto
  showSaveProjectModal = false;
  projectName = '';
  projectColor = '#3b82f6'; // Color por defecto (azul)
  isSavingProject = false;
  
  elementProperties: any = {
    // Propiedades generales
    left: 0,
    top: 0,
    angle: 0,
    scaleX: 1,
    scaleY: 1,
    opacity: 1,
    // Propiedades de texto
    text: '',
    fontSize: 24,
    fontFamily: 'Arial',
    fill: '#000000', // Color de texto o relleno de forma
    fontWeight: 'normal',
    fontStyle: 'normal',
    underline: false,
    // Propiedades de formas
    stroke: '#000000',
    strokeWidth: 2,
    // Propiedades específicas de formas
    width: 0,
    height: 0,
    radius: 0
  };

  // Proyecto actual
  proyectoId: string | null = null;
  private autoSaveInterval = 5000; // Guardar cada 5 segundos
  
  // Seguimiento de cambios no guardados
  hasUnsavedChanges = false;
  private lastSavedState: string = '';
  
  // Configuración de texto
  textContent = '';
  selectedFont = 'Arial';
  fontSize = 24;
  textColor = '#000000';
  textBold = false;
  textItalic = false;
  textUnderline = false;
  
  // Fuentes disponibles (60+ fuentes)
  fonts = [
    'Arial',
    'Arial Black',
    'Arial Narrow',
    'Arial Rounded MT Bold',
    'Baskerville',
    'Bodoni MT',
    'Book Antiqua',
    'Calibri',
    'Cambria',
    'Century Gothic',
    'Comic Sans MS',
    'Consolas',
    'Courier New',
    'Franklin Gothic Medium',
    'Garamond',
    'Georgia',
    'Gill Sans',
    'Helvetica',
    'Impact',
    'Lucida Console',
    'Lucida Sans Unicode',
    'Microsoft Sans Serif',
    'Monaco',
    'Palatino',
    'Segoe UI',
    'Tahoma',
    'Times New Roman',
    'Trebuchet MS',
    'Verdana',
    'Brush Script MT',
    'Copperplate',
    'Didot',
    'Futura',
    'Geneva',
    'Goudy Old Style',
    'Hoefler Text',
    'Lucida Grande',
    'Optima',
    'Perpetua',
    'Rockwell',
    'Bookman',
    'Chalkduster',
    'Courier',
    'Helvetica Neue',
    'Lucida Handwriting',
    'Marker Felt',
    'Menlo',
    'Monaco',
    'Papyrus',
    'Snell Roundhand',
    'Zapfino',
    'Andale Mono',
    'Apple Chancery',
    'Bradley Hand',
    'Chalkboard',
    'Cochin',
    'Futura',
    'Gill Sans',
    'Herculanum',
    'Luminari',
    'Marker Felt',
    'Noteworthy',
    'Skia',
    'Tekton',
    'Trattatello'
  ];
  
  // Tipos de formas disponibles
  shapeTypes = [
    { id: 'rect', name: 'Rectángulo', icon: '⬜' },
    { id: 'circle', name: 'Círculo', icon: '⭕' },
    { id: 'triangle', name: 'Triángulo', icon: '🔺' },
    { id: 'line', name: 'Línea', icon: '➖' },
    { id: 'ellipse', name: 'Elipse', icon: '🔵' },
    { id: 'polygon', name: 'Polígono', icon: '⬡' }
  ];
  
  selectedShapeType = 'rect';
  shapeColor = '#FF0000';
  shapeStrokeColor = '#000000';
  shapeStrokeWidth = 2;
  
  // Estado de carga de imagen
  isUploadingImage = false;
  // FUNCIONALIDAD DE ELIMINAR FONDO TEMPORALMENTE DESHABILITADA
  // isRemovingBackground = false;
  // backgroundRemovalProgress = 0;
  // removeBackgroundOnUpload = false; // Checkbox para eliminar fondo al subir
  isRemovingBackground = false; // Mantener para evitar errores de referencia
  backgroundRemovalProgress = 0; // Mantener para evitar errores de referencia
  removeBackgroundOnUpload = false; // Mantener para evitar errores de referencia
  
  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private proyectosService: ProyectosService,
    private authService: AuthService
  ) { }

  private proyectoPendienteCarga: string | null = null;

  ngOnInit(): void {
    this.detectTheme();
    this.observeThemeChanges();
    
    // Leer proyectoId de los query params
    this.route.queryParams.subscribe(params => {
      const nuevoProyectoId = params['proyectoId'] || null;
      
      // Si cambió el proyectoId
      if (nuevoProyectoId !== this.proyectoId) {
        this.proyectoId = nuevoProyectoId;
        
        if (this.proyectoId) {
          // Si hay proyectoId, cargar el proyecto
          if (this.canvas) {
            this.cargarProyecto();
          } else {
            // Guardar para cargar después de inicializar el canvas
            this.proyectoPendienteCarga = this.proyectoId;
          }
        } else {
          // Si NO hay proyectoId, limpiar el canvas (editor limpio)
          if (this.canvas) {
            this.limpiarCanvasParaNuevoProyecto();
          } else {
            // Marcar que no hay proyecto pendiente
            this.proyectoPendienteCarga = null;
          }
        }
      }
    });
    
    // Escuchar evento de selección desde el árbol
    window.addEventListener('selectElementFromTree', this.handleSelectElementFromTree.bind(this));
  }
  
  // Manejar selección de elemento desde el árbol
  private handleSelectElementFromTree(event: any) {
    const { proyectoId, elementIndex, elementName } = event.detail;
    
    // Verificar que tenemos canvas
    if (!this.canvas) {
      console.warn('Canvas no está inicializado');
      return;
    }
    
    // Si hay proyectoId, verificar que coincida con el proyecto actual
    // Si no hay proyectoId en el banner-editor, también permitir la selección (proyecto nuevo)
    if (proyectoId && this.proyectoId && proyectoId !== this.proyectoId) {
      console.log('Proyecto no coincide:', { proyectoId, currentProyectoId: this.proyectoId });
      return;
    }
    
    // Obtener todos los objetos del canvas
    const objects = this.canvas.getObjects();
    
    if (objects.length === 0) {
      console.warn('No hay elementos en el canvas');
      return;
    }
    
    let element = null;
    
    // Intentar encontrar el elemento por nombre primero (más confiable)
    // Usar la misma lógica que updateProjectElementsInSidebar para generar nombres
    if (elementName) {
      for (let i = 0; i < objects.length; i++) {
        const obj = objects[i];
        let objName = '';
        
        // Generar el nombre del objeto usando la misma lógica que updateProjectElementsInSidebar
        if (obj instanceof fabric.Text) {
          objName = obj.text || `Texto ${i + 1}`;
        } else if (obj instanceof fabric.Image) {
          objName = `Imagen ${i + 1}`;
        } else if (obj instanceof fabric.Rect) {
          objName = `Rectángulo ${i + 1}`;
        } else if (obj instanceof fabric.Circle) {
          objName = `Círculo ${i + 1}`;
        } else if (obj instanceof fabric.Triangle) {
          objName = `Triángulo ${i + 1}`;
        } else if (obj instanceof fabric.Line) {
          objName = `Línea ${i + 1}`;
        } else if (obj instanceof fabric.Ellipse) {
          objName = `Elipse ${i + 1}`;
        } else if (obj instanceof fabric.Polygon) {
          objName = `Polígono ${i + 1}`;
        } else {
          objName = `Forma ${i + 1}`;
        }
        
        // Comparar nombres (normalizar espacios)
        const normalizedObjName = objName.trim();
        const normalizedElementName = elementName.trim();
        
        // Para elementos de texto, comparar solo el texto sin el prefijo "Texto X"
        if (obj instanceof fabric.Text && obj.text) {
          const textOnly = obj.text.trim();
          if (textOnly === normalizedElementName || normalizedObjName === normalizedElementName) {
            element = obj;
            break;
          }
        } else if (normalizedObjName === normalizedElementName) {
          element = obj;
          break;
        }
      }
    }
    
    // Si no se encontró por nombre, usar el índice como fallback
    if (!element && elementIndex >= 0 && elementIndex < objects.length) {
      element = objects[elementIndex];
    }
    
    // Seleccionar el elemento en el canvas y abrir propiedades
    if (element) {
      this.canvas.setActiveObject(element);
      this.canvas.renderAll();
      
      // Abrir el panel de propiedades
      this.openElementPropertiesModal(element);
    } else {
      console.warn('No se pudo encontrar el elemento para seleccionar:', { 
        elementIndex, 
        elementName, 
        objectsCount: objects.length,
        proyectoId,
        currentProyectoId: this.proyectoId
      });
    }
  }
  
  // Actualizar selección en el árbol cuando se selecciona un elemento en el canvas
  private updateElementSelectionInTree(element: any) {
    if (!this.proyectoId || !this.canvas) return;
    
    const objects = this.canvas.getObjects();
    const index = objects.indexOf(element);
    
    if (index >= 0) {
      // Emitir evento para actualizar la selección en el árbol
      const event = new CustomEvent('updateElementSelectionInTree', {
        detail: {
          proyectoId: this.proyectoId,
          elementIndex: index
        }
      });
      window.dispatchEvent(event);
    }
  }
  
  // Limpiar selección en el árbol cuando se deselecciona en el canvas
  private clearElementSelectionInTree() {
    if (!this.proyectoId) return;
    
    // Emitir evento para limpiar la selección en el árbol
    const event = new CustomEvent('clearElementSelectionInTree', {
      detail: {
        proyectoId: this.proyectoId
      }
    });
    window.dispatchEvent(event);
  }

  ngAfterViewInit(): void {
    // Esperar un ciclo para asegurar que el DOM esté completamente renderizado
    setTimeout(() => {
      this.initializeCanvas();
      // Si hay un proyecto pendiente de cargar, cargarlo ahora
      if (this.proyectoPendienteCarga) {
        setTimeout(() => {
          this.cargarProyecto();
          this.proyectoPendienteCarga = null;
        }, 100);
      } else if (!this.proyectoId) {
        // Si no hay proyectoId, asegurar que el canvas esté limpio
        setTimeout(() => {
          this.limpiarCanvasParaNuevoProyecto();
        }, 100);
      }
    }, 0);
  }

  // Métodos para seguimiento de cambios
  private markAsChanged() {
    this.hasUnsavedChanges = true;
    this.updateProjectElementsInSidebar();
  }
  
  private markAsSaved() {
    this.hasUnsavedChanges = false;
    this.saveCurrentState();
    this.updateProjectElementsInSidebar();
  }
  
  private saveCurrentState() {
    if (!this.canvas) return;
    try {
      const elements = this.canvas.getObjects();
      this.lastSavedState = JSON.stringify({
        count: elements.length,
        timestamp: Date.now()
      });
    } catch (e) {
      console.error('Error guardando estado:', e);
    }
  }
  
  // Actualizar lista de elementos del proyecto en el sidebar
  private updateProjectElementsInSidebar() {
    if (!this.proyectoId || !this.canvas) return;
    
    // Usar la misma lógica que updateElementsList() para generar nombres consistentes
    const event = new CustomEvent('projectElementsUpdated', {
      detail: {
        proyectoId: this.proyectoId,
        elements: this.canvas.getObjects().map((obj: any, index: number) => {
          let name = '';
          let type: 'text' | 'image' | 'shape' = 'text';
          
          if (obj instanceof fabric.Text) {
            name = obj.text || `Texto ${index + 1}`;
            type = 'text';
          } else if (obj instanceof fabric.Image) {
            name = `Imagen ${index + 1}`;
            type = 'image';
          } else if (obj instanceof fabric.Rect || 
                     obj instanceof fabric.Circle || 
                     obj instanceof fabric.Triangle ||
                     obj instanceof fabric.Line ||
                     obj instanceof fabric.Ellipse ||
                     obj instanceof fabric.Polygon) {
            // Determinar el nombre según el tipo de forma (igual que en updateElementsList)
            if (obj instanceof fabric.Rect) {
              name = `Rectángulo ${index + 1}`;
            } else if (obj instanceof fabric.Circle) {
              name = `Círculo ${index + 1}`;
            } else if (obj instanceof fabric.Triangle) {
              name = `Triángulo ${index + 1}`;
            } else if (obj instanceof fabric.Line) {
              name = `Línea ${index + 1}`;
            } else if (obj instanceof fabric.Ellipse) {
              name = `Elipse ${index + 1}`;
            } else if (obj instanceof fabric.Polygon) {
              name = `Polígono ${index + 1}`;
            } else {
              name = `Forma ${index + 1}`;
            }
            type = 'shape';
          } else {
            name = `Elemento ${index + 1}`;
            type = 'text'; // fallback
          }
          
          return { 
            name, 
            type: type,
            text: obj instanceof fabric.Text ? obj.text : undefined,
            shapeType: obj instanceof fabric.Rect ? 'rect' :
                      obj instanceof fabric.Circle ? 'circle' :
                      obj instanceof fabric.Triangle ? 'triangle' :
                      obj instanceof fabric.Line ? 'line' :
                      obj instanceof fabric.Ellipse ? 'ellipse' :
                      obj instanceof fabric.Polygon ? 'polygon' : undefined
          };
        })
      }
    });
    window.dispatchEvent(event);
  }
  
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges && this.proyectoId) {
      event.preventDefault();
      event.returnValue = 'Tienes cambios sin guardar. ¿Estás seguro de que quieres salir?';
      return event.returnValue;
    }
  }
  
  // Método para verificar cambios antes de navegar
  canDeactivate(): boolean {
    if (this.hasUnsavedChanges && this.proyectoId) {
      return confirm('Tienes cambios sin guardar. ¿Estás seguro de que quieres salir? Los cambios se perderán si no los guardas.');
    }
    return true;
  }

  ngOnDestroy(): void {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    if (this.canvas) {
      this.canvas.dispose();
    }
    // Limpiar timeout de notificación
    if (this.saveNotificationTimeout) {
      clearTimeout(this.saveNotificationTimeout);
    }
    // Limpiar listener de selección desde el árbol
    window.removeEventListener('selectElementFromTree', this.handleSelectElementFromTree.bind(this));
  }

  private detectTheme() {
    this.isDarkMode = document.body.classList.contains('dark-theme');
  }

  private observeThemeChanges() {
    this.themeObserver = new MutationObserver(() => {
      this.detectTheme();
      // Actualizar colores del canvas según el tema si es necesario
    });
    
    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private initializeCanvas() {
    const canvasElement = document.getElementById('banner-canvas') as HTMLCanvasElement;
    if (!canvasElement) {
      console.error('Canvas element not found');
      return;
    }

    // Inicializar el canvas con el fondo correcto
    if (this.backgroundType === 'gradient') {
      // Si es degradado, inicializar sin color de fondo y luego aplicar el degradado
      this.canvas = new fabric.Canvas(canvasElement, {
        width: this.canvasWidth,
        height: this.canvasHeight
      });
      this.applyBackground();
    } else {
      // Si es color sólido, inicializar con el color
      this.canvas = new fabric.Canvas(canvasElement, {
        width: this.canvasWidth,
        height: this.canvasHeight,
        backgroundColor: this.backgroundColor
      });
    }

    // Aplicar esquinas redondeadas si están habilitadas
    this.updateCanvasBorderRadius();
    
    // Aplicar borde si está habilitado
    this.updateCanvasBorder();

    // Hacer todos los objetos arrastrables
    this.canvas.on('object:added', (e: any) => {
      if (e.target) {
        e.target.set({
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true
        });
        this.updateElementsList();
      }
    });

    // Actualizar lista cuando se mueve un objeto
    this.canvas.on('object:modified', () => {
      this.updateElementsList();
      this.markAsChanged();
    });

    // Actualizar lista cuando se elimina un objeto
    this.canvas.on('object:removed', () => {
      this.updateElementsList();
      this.markAsChanged();
    });
    
    // Detectar cuando se agrega un objeto (ya existe, pero agregamos markAsChanged)
    this.canvas.on('object:added', (e: any) => {
      if (e.target) {
        this.markAsChanged();
      }
    });

    // Evento de doble clic para abrir propiedades
    this.canvas.on('mouse:dblclick', (e: any) => {
      if (e.target) {
        this.openElementPropertiesModal(e.target);
      }
    });

    // Evento de selección para abrir propiedades automáticamente
    this.canvas.on('selection:created', (e: any) => {
      if (e.selected && e.selected.length === 1) {
        this.openElementPropertiesModal(e.selected[0]);
        this.updateElementSelectionInTree(e.selected[0]);
      }
    });

    this.canvas.on('selection:updated', (e: any) => {
      if (e.selected && e.selected.length === 1) {
        this.openElementPropertiesModal(e.selected[0]);
        this.updateElementSelectionInTree(e.selected[0]);
      }
    });

    this.canvas.on('selection:cleared', () => {
      this.closeElementPropertiesModal();
      this.clearElementSelectionInTree();
    });

    // Configurar eventos de teclado para copiar y pegar
    this.setupKeyboardShortcuts();

    // Agregar listener de paste directamente al canvas
    this.canvas.getElement().addEventListener('paste', (e: ClipboardEvent) => {
      // Solo procesar si no estamos en un input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      e.preventDefault();
      this.handlePasteEvent(e);
    });
  }

  private handlePasteEvent(e: ClipboardEvent) {
    if (!this.canvas) {
      return;
    }

    // Si hay un objeto copiado del canvas, no procesar aquí
    if (this.copiedObject) {
      return; // El método pasteObject lo manejará
    }

    const clipboardData = e.clipboardData;
    if (!clipboardData) {
      return;
    }

    // Verificar si hay imágenes primero
    const items = clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Si es una imagen
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        if (blob) {
          const imageUrl = URL.createObjectURL(blob);
          this.pasteImageAsElement(imageUrl);
          return;
        }
      }
    }

    // Si no hay imágenes, intentar texto
    const pastedText = clipboardData.getData('text');
    if (pastedText && pastedText.trim()) {
      this.pasteTextAsElement(pastedText);
    }
  }

  private setupKeyboardShortcuts() {
    // Event listener para Ctrl+C y Ctrl+V
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      // Solo procesar si el canvas está activo y no estamos en un input
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return; // Permitir el comportamiento normal en inputs
      }

      // Ctrl+C o Cmd+C (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        this.copySelectedObject();
      }

      // Ctrl+V o Cmd+V (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.preventDefault(); // Prevenir el comportamiento predeterminado
        this.pasteObject();
      }
    });
  }

  private copySelectedObject() {
    if (!this.canvas) {
      return;
    }

    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      // Clonar el objeto usando fabric.js
      activeObject.clone((cloned: any) => {
        this.copiedObject = cloned;
        console.log('Objeto copiado');
      });
    }
  }

  private async pasteObject() {
    if (!this.canvas) {
      return;
    }

    // Si hay un objeto copiado, pegarlo (duplicarlo)
    if (this.copiedObject) {
      this.copiedObject.clone((cloned: any) => {
        // Mover el objeto duplicado ligeramente para que no se superponga
        cloned.set({
          left: (cloned.left || 0) + 20,
          top: (cloned.top || 0) + 20
        });
        
        // Restaurar propiedades de selección
        cloned.set({
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true
        });

        this.canvas.add(cloned);
        this.canvas.setActiveObject(cloned);
        this.canvas.renderAll();
        this.updateElementsList();
      });
      return;
    }

    // Intentar pegar desde el portapapeles (imágenes o texto)
    try {
      const clipboardItems = await navigator.clipboard.read();
      
      // Buscar imágenes primero
      for (const clipboardItem of clipboardItems) {
        // Verificar si hay imágenes disponibles
        const imageTypes = clipboardItem.types.filter(type => type.startsWith('image/'));
        
        if (imageTypes.length > 0) {
          // Obtener la imagen del portapapeles
          const imageBlob = await clipboardItem.getType(imageTypes[0]);
          const imageUrl = URL.createObjectURL(imageBlob);
          this.pasteImageAsElement(imageUrl);
          return;
        }
      }
      
      // Si no hay imágenes, intentar texto
      const text = await navigator.clipboard.readText();
      if (text && text.trim()) {
        this.pasteTextAsElement(text);
      }
    } catch (error) {
      // Fallback: usar el evento de paste del documento
      console.log('No se pudo leer el portapapeles con Clipboard API, usando método alternativo');
      this.capturePaste();
    }
  }

  private capturePaste() {
    // Crear un contenedor temporal para capturar el evento de paste
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'fixed';
    tempDiv.style.opacity = '0';
    tempDiv.style.pointerEvents = 'none';
    tempDiv.setAttribute('contenteditable', 'true');
    document.body.appendChild(tempDiv);
    tempDiv.focus();

    const pasteHandler = async (e: ClipboardEvent) => {
      e.preventDefault();
      
      const clipboardData = e.clipboardData;
      if (!clipboardData) {
        return;
      }

      // Verificar si hay imágenes
      const items = clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Si es una imagen
        if (item.type.indexOf('image') !== -1) {
          const blob = item.getAsFile();
          if (blob) {
            const imageUrl = URL.createObjectURL(blob);
            this.pasteImageAsElement(imageUrl);
            document.removeEventListener('paste', pasteHandler);
            document.body.removeChild(tempDiv);
            return;
          }
        }
      }

      // Si no hay imágenes, intentar texto
      const pastedText = clipboardData.getData('text');
      if (pastedText && pastedText.trim()) {
        this.pasteTextAsElement(pastedText);
      }
      
      document.removeEventListener('paste', pasteHandler);
      document.body.removeChild(tempDiv);
    };

    document.addEventListener('paste', pasteHandler);
    
    // Timeout de seguridad
    setTimeout(() => {
      document.removeEventListener('paste', pasteHandler);
      if (document.body.contains(tempDiv)) {
        document.body.removeChild(tempDiv);
      }
    }, 1000);
  }

  private pasteTextAsElement(text: string) {
    if (!this.canvas || !text.trim()) {
      return;
    }

    // Crear un nuevo elemento de texto en el centro del canvas
    const textElement = new fabric.Text(text.trim(), {
      left: this.canvasWidth / 2,
      top: this.canvasHeight / 2,
      fontSize: this.fontSize,
      fontFamily: this.selectedFont,
      fill: this.textColor,
      originX: 'center',
      originY: 'center',
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true
    });

    this.canvas.add(textElement);
    this.canvas.setActiveObject(textElement);
    this.canvas.renderAll();
    this.updateElementsList();
  }

  private pasteImageAsElement(imageUrl: string) {
    if (!this.canvas || !imageUrl) {
      return;
    }

    this.isUploadingImage = true;

    fabric.Image.fromURL(imageUrl, (img: any) => {
      // Ajustar tamaño si es muy grande
      const maxWidth = this.canvasWidth - 40;
      const maxHeight = this.canvasHeight - 40;
      
      if (img.width > maxWidth || img.height > maxHeight) {
        const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
        img.scale(scale);
      }

      // Centrar en el canvas
      img.set({
        left: this.canvasWidth / 2,
        top: this.canvasHeight / 2,
        originX: 'center',
        originY: 'center',
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true
      });

      this.canvas.add(img);
      this.canvas.setActiveObject(img);
      this.canvas.renderAll();
      
      this.isUploadingImage = false;
      this.updateElementsList();
      
      // Liberar la URL del objeto
      URL.revokeObjectURL(imageUrl);
    }, { crossOrigin: 'anonymous' });
  }

  toggleTemplateDropdown() {
    this.showTemplateDropdown = !this.showTemplateDropdown;
  }

  selectTemplate(templateId: string) {
    this.selectedTemplate = templateId;
    this.showTemplateDropdown = false;
    this.onTemplateChange();
  }

  getTemplateIconClass(templateId: string): string {
    const iconMap: { [key: string]: string } = {
      'portada-web': 'icon-2-1',
      'banner-rectangular': 'icon-191-1',
      'flyer-cuadrado': 'icon-1-1',
      'flyer-rectangular': 'icon-3-2'
    };
    return iconMap[templateId] || '';
  }

  onTemplateChange() {
    if (this.selectedTemplate === 'personalizado') {
      // Si es personalizado, regresar a las medidas iniciales
      this.canvasWidth = 1000;
      this.canvasHeight = 400;
      this.onCanvasSizeChange();
      return;
    }
    
    const template = this.templates.find(t => t.id === this.selectedTemplate);
    if (template && template.width && template.height) {
      this.canvasWidth = template.width;
      this.canvasHeight = template.height;
      this.onCanvasSizeChange();
    }
  }
  
  onCanvasSizeChange() {
    if (!this.canvas) {
      return;
    }

    // Si el usuario cambia manualmente las dimensiones, cambiar a personalizado
    const currentTemplate = this.templates.find(t => 
      t.width === this.canvasWidth && t.height === this.canvasHeight && t.id !== 'personalizado'
    );
    if (!currentTemplate) {
      this.selectedTemplate = 'personalizado';
    }

    // Validar dimensiones mínimas y máximas
    if (this.canvasWidth < 100) {
      this.canvasWidth = 100;
    }
    if (this.canvasWidth > 5000) {
      this.canvasWidth = 5000;
    }
    if (this.canvasHeight < 100) {
      this.canvasHeight = 100;
    }
    if (this.canvasHeight > 5000) {
      this.canvasHeight = 5000;
    }

    // Guardar los objetos actuales del canvas
    const objects = this.canvas.getObjects();

    // Redimensionar el canvas
    this.canvas.setDimensions({
      width: this.canvasWidth,
      height: this.canvasHeight
    });

    // Restaurar los objetos (fabric.js los mantiene automáticamente, pero asegurémonos)
    objects.forEach((obj: any) => {
      // Asegurar que los objetos no queden fuera de los nuevos límites
      if (obj.left > this.canvasWidth) {
        obj.left = this.canvasWidth - (obj.width * obj.scaleX || 50);
      }
      if (obj.top > this.canvasHeight) {
        obj.top = this.canvasHeight - (obj.height * obj.scaleY || 50);
      }
      // Limitar a los límites mínimos
      if (obj.left < 0) obj.left = 0;
      if (obj.top < 0) obj.top = 0;
    });

    // Reaplicar el fondo con las nuevas dimensiones
    this.applyBackground();

    // Actualizar el borde si está habilitado
    this.updateCanvasBorder();

    // Actualizar las esquinas redondeadas si están habilitadas
    this.updateCanvasBorderRadius();

    // Renderizar el canvas
    this.canvas.renderAll();
    this.onCanvasPropertyChange();
  }

  onBackgroundColorChange(color: string) {
    this.backgroundColor = color;
    this.applyBackground();
    this.onCanvasPropertyChange();
  }

  onBackgroundTypeChange() {
    this.applyBackground();
    this.onCanvasPropertyChange();
  }

  onGradientChange() {
    if (this.backgroundType === 'gradient') {
      this.applyBackground();
      this.onCanvasPropertyChange();
    }
  }

  private applyBackground() {
    if (!this.canvas) {
      return;
    }

    if (this.backgroundType === 'solid') {
      // Fondo sólido
      this.canvas.setBackgroundColor(this.backgroundColor, this.canvas.renderAll.bind(this.canvas));
    } else {
      // Fondo degradado
      this.applyGradientBackground();
    }
  }

  private applyGradientBackground() {
    if (!this.canvas) {
      return;
    }

    // Definir las coordenadas del degradado según la dirección
    let coords: { x1: number; y1: number; x2: number; y2: number };

    switch (this.gradientDirection) {
      case 'horizontal':
        // Degradado horizontal (izquierda a derecha)
        coords = {
          x1: 0,
          y1: 0,
          x2: this.canvasWidth,
          y2: 0
        };
        break;
      case 'vertical':
        // Degradado vertical (arriba a abajo)
        coords = {
          x1: 0,
          y1: 0,
          x2: 0,
          y2: this.canvasHeight
        };
        break;
      case 'diagonal':
        // Degradado diagonal (esquina superior izquierda a inferior derecha)
        coords = {
          x1: 0,
          y1: 0,
          x2: this.canvasWidth,
          y2: this.canvasHeight
        };
        break;
      default:
        coords = {
          x1: 0,
          y1: 0,
          x2: this.canvasWidth,
          y2: 0
        };
    }

    // Crear el degradado
    const gradient = new fabric.Gradient({
      type: 'linear',
      coords: coords,
      colorStops: [
        { offset: 0, color: this.gradientColorStart },
        { offset: 1, color: this.gradientColorEnd }
      ]
    });

    // Aplicar el degradado como fondo
    this.canvas.setBackgroundColor(gradient as any, this.canvas.renderAll.bind(this.canvas));
  }

  addText() {
    if (!this.textContent.trim() || !this.canvas) {
      return;
    }

    const text = new fabric.Text(this.textContent, {
      left: 100,
      top: 100,
      fontSize: this.fontSize,
      fontFamily: this.selectedFont,
      fill: this.textColor,
      fontWeight: this.textBold ? 'bold' : 'normal',
      fontStyle: this.textItalic ? 'italic' : 'normal',
      underline: this.textUnderline,
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true
    });
    
    this.canvas.add(text);
    this.canvas.setActiveObject(text);
    this.canvas.renderAll();

    // Limpiar el input de texto
    this.textContent = '';
    this.markAsChanged();
    this.updateElementsList();
  }

  onImageSelected(event: any) {
    const file = event.target.files[0];
    if (!file || !this.canvas) {
      return;
    }

    // Validar que sea una imagen
    if (!file.type.match('image.*')) {
      alert('Por favor, selecciona un archivo de imagen válido');
      return;
    }

    this.isUploadingImage = true;
    this.backgroundRemovalProgress = 0;

    // FUNCIONALIDAD DE ELIMINAR FONDO TEMPORALMENTE DESHABILITADA
    // Si está marcado eliminar fondo, procesar primero
    // if (this.removeBackgroundOnUpload) {
    //   this.isRemovingBackground = true;
    //   this.backgroundRemovalProgress = 10;

    //   // Llamar al servicio para eliminar el fondo
    //   this.proyectosService.removeBackground(file, 'birefnet-massive').subscribe({
    //     next: (result) => {
    //       this.backgroundRemovalProgress = 60;
          
    //       // Descargar la imagen procesada para subirla
    //       fetch(result.processed_url)
    //         .then(response => response.blob())
    //         .then(blob => {
    //           // Crear un nuevo File con la imagen procesada
    //           const processedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.png'), { type: 'image/png' });
              
    //           // Subir la imagen procesada al storage
    //           this.backgroundRemovalProgress = 70;
    //           this.proyectosService.uploadImage(processedFile).subscribe({
    //             next: (uploadResponse) => {
    //               this.backgroundRemovalProgress = 90;
    //               this._loadImageToCanvas(uploadResponse.url, result.processed_url);
    //             },
    //             error: (error) => {
    //               console.error('Error subiendo imagen procesada:', error);
    //               // Si falla la subida, intentar usar directamente la URL procesada
    //               this._loadImageToCanvas(result.processed_url, result.processed_url);
    //             }
    //           });
    //         })
    //         .catch(error => {
    //           console.error('Error descargando imagen procesada:', error);
    //           alert('Error al procesar la imagen. Por favor, intenta nuevamente.');
    //           this.isUploadingImage = false;
    //           this.isRemovingBackground = false;
    //           this.backgroundRemovalProgress = 0;
    //         });
    //     },
    //     error: (error) => {
    //       console.error('Error eliminando fondo:', error);
    //       alert('Error al eliminar el fondo de la imagen. Por favor, intenta nuevamente.');
    //       this.isUploadingImage = false;
    //       this.isRemovingBackground = false;
    //       this.backgroundRemovalProgress = 0;
    //     }
    //   });
    // } else {
    //   // Subir imagen original sin procesar
    //   this.backgroundRemovalProgress = 30;
    //   this.proyectosService.uploadImage(file).subscribe({
    //     next: (response) => {
    //       this.backgroundRemovalProgress = 80;
    //       this._loadImageToCanvas(response.url, response.url);
    //     },
    //     error: (error) => {
    //       console.error('Error subiendo imagen:', error);
    //       alert('Error al subir la imagen. Por favor, intenta nuevamente.');
    //       this.isUploadingImage = false;
    //       this.backgroundRemovalProgress = 0;
    //     }
    //   });
    // }
    
    // FUNCIONALIDAD DE ELIMINAR FONDO DESHABILITADA - Subir imagen directamente sin procesar
    this.backgroundRemovalProgress = 30;
    this.proyectosService.uploadImage(file).subscribe({
      next: (response) => {
        this.backgroundRemovalProgress = 80;
        this._loadImageToCanvas(response.url, response.url);
      },
      error: (error) => {
        console.error('Error subiendo imagen:', error);
        alert('Error al subir la imagen. Por favor, intenta nuevamente.');
        this.isUploadingImage = false;
        this.backgroundRemovalProgress = 0;
      }
    });
    
    // Limpiar el input
    event.target.value = '';
  }

  private _loadImageToCanvas(imageUrl: string, storageUrl: string) {
    // Cargar imagen desde la URL del storage
    // Usar timeout para detectar si la imagen no carga
    const loadTimeout = setTimeout(() => {
      if (this.isUploadingImage) {
        console.error('Timeout cargando imagen:', imageUrl);
        this.isUploadingImage = false;
        this.isRemovingBackground = false;
        this.backgroundRemovalProgress = 0;
        alert('La imagen tardó demasiado en cargar. Por favor, verifica la conexión e intenta nuevamente.');
      }
    }, 30000); // 30 segundos de timeout

    fabric.Image.fromURL(imageUrl, (img: any) => {
      clearTimeout(loadTimeout);
      
      // Verificar que la imagen se cargó correctamente
      if (!img || !img.width || !img.height) {
        console.error('Error: Imagen inválida cargada');
        this.isUploadingImage = false;
        this.isRemovingBackground = false;
        this.backgroundRemovalProgress = 0;
        alert('Error al cargar la imagen. La imagen puede estar corrupta o en un formato no soportado.');
        return;
      }

      // Guardar la URL del storage en el objeto de imagen
      img.set('storageUrl', storageUrl);
      
      // Ajustar tamaño si es muy grande
      const maxWidth = this.canvasWidth - 40;
      const maxHeight = this.canvasHeight - 40;
      
      if (img.width > maxWidth || img.height > maxHeight) {
        const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
        img.scale(scale);
      }

      // Centrar en el canvas
      img.set({
        left: (this.canvasWidth - img.width * img.scaleX) / 2,
        top: (this.canvasHeight - img.height * img.scaleY) / 2,
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true
      });

      this.canvas.add(img);
      this.canvas.setActiveObject(img);
      this.canvas.renderAll();
      
      this.backgroundRemovalProgress = 100;
      this.isUploadingImage = false;
      this.isRemovingBackground = false;
      this.updateElementsList();
      this.markAsChanged();
      
      // Ocultar la barra de progreso después de un momento
      setTimeout(() => {
        this.backgroundRemovalProgress = 0;
      }, 1000);
    }, { 
      crossOrigin: 'anonymous' 
    });
  }

  private updateElementsList() {
    this.canvasElements = [];
    
    if (!this.canvas) return;

    this.canvas.getObjects().forEach((obj: any, index: number) => {
      let name = '';
      let type: 'text' | 'image' | 'shape' = 'text';

      if (obj instanceof fabric.Text) {
        name = obj.text || `Texto ${index + 1}`;
        type = 'text';
      } else if (obj instanceof fabric.Image) {
        name = `Imagen ${index + 1}`;
        type = 'image';
      } else if (obj instanceof fabric.Rect || 
                 obj instanceof fabric.Circle || 
                 obj instanceof fabric.Triangle ||
                 obj instanceof fabric.Line ||
                 obj instanceof fabric.Ellipse ||
                 obj instanceof fabric.Polygon) {
        // Determinar el nombre según el tipo de forma
        if (obj instanceof fabric.Rect) {
          name = `Rectángulo ${index + 1}`;
        } else if (obj instanceof fabric.Circle) {
          name = `Círculo ${index + 1}`;
        } else if (obj instanceof fabric.Triangle) {
          name = `Triángulo ${index + 1}`;
        } else if (obj instanceof fabric.Line) {
          name = `Línea ${index + 1}`;
        } else if (obj instanceof fabric.Ellipse) {
          name = `Elipse ${index + 1}`;
        } else if (obj instanceof fabric.Polygon) {
          name = `Polígono ${index + 1}`;
        } else {
          name = `Forma ${index + 1}`;
        }
        type = 'shape';
      }

      this.canvasElements.push({
        id: `element-${index}`,
        type: type,
        name: name,
        object: obj
      });
    });

    this.cdr.detectChanges();
    
    // Actualizar lista de elementos en el sidebar
    this.updateProjectElementsInSidebar();
  }

  selectElement(element: CanvasElement) {
    if (this.canvas && element.object) {
      this.canvas.setActiveObject(element.object);
      this.canvas.renderAll();
    }
  }

  deleteElement(element: CanvasElement) {
    if (this.canvas && element.object) {
      this.canvas.remove(element.object);
      this.canvas.renderAll();
      this.updateElementsList();
    }
  }

  toggleElementVisibility(element: CanvasElement) {
    if (this.canvas && element.object) {
      element.object.visible = !element.object.visible;
      this.canvas.renderAll();
    }
  }

  isElementVisible(element: CanvasElement): boolean {
    if (!element.object) {
      return true;
    }
    return element.object.visible !== false; // Por defecto los objetos son visibles
  }

  downloadBanner() {
    if (!this.canvas) {
      return;
    }

    // Calcular dimensiones del canvas final (incluyendo borde si está habilitado)
    const borderSize = this.enableBorder ? this.borderWidth : 0;
    const finalWidth = this.canvasWidth + (borderSize * 2);
    const finalHeight = this.canvasHeight + (borderSize * 2);

    // Crear canvas temporal para la exportación
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = finalWidth;
    tempCanvas.height = finalHeight;
    const ctx = tempCanvas.getContext('2d');

    if (!ctx) {
      // Fallback si no se puede crear el contexto
      const qualityValue = this.jpgQuality / 100; // Convertir porcentaje (0-100) a decimal (0-1)
      const dataURL = this.canvas.toDataURL({
        format: 'jpeg',
        quality: qualityValue,
        multiplier: 1
      });
      this.downloadImage(dataURL);
      return;
    }

    // Si hay borde, dibujarlo primero como fondo
    if (this.enableBorder && this.borderWidth > 0) {
      if (this.roundedCorners && this.cornerRadius > 0) {
        // Borde con esquinas redondeadas
        const radius = this.cornerRadius + borderSize;
        ctx.fillStyle = this.borderColor;
        ctx.beginPath();
        ctx.moveTo(radius, 0);
        ctx.lineTo(finalWidth - radius, 0);
        ctx.quadraticCurveTo(finalWidth, 0, finalWidth, radius);
        ctx.lineTo(finalWidth, finalHeight - radius);
        ctx.quadraticCurveTo(finalWidth, finalHeight, finalWidth - radius, finalHeight);
        ctx.lineTo(radius, finalHeight);
        ctx.quadraticCurveTo(0, finalHeight, 0, finalHeight - radius);
        ctx.lineTo(0, radius);
        ctx.quadraticCurveTo(0, 0, radius, 0);
        ctx.closePath();
        ctx.fill();
      } else {
        // Borde recto
        ctx.fillStyle = this.borderColor;
        ctx.fillRect(0, 0, finalWidth, finalHeight);
      }
    }

    // Dibujar el fondo del canvas primero (JPG siempre necesita fondo)
    if (this.backgroundType === 'solid') {
      ctx.fillStyle = this.backgroundColor;
    } else {
      // Para degradado, necesitamos crear un gradiente
      const gradient = ctx.createLinearGradient(
        borderSize, borderSize,
        borderSize + this.canvasWidth, borderSize + this.canvasHeight
      );
      gradient.addColorStop(0, this.gradientColorStart);
      gradient.addColorStop(1, this.gradientColorEnd);
      ctx.fillStyle = gradient;
    }

    // Crear área de recorte para el contenido del canvas (si hay esquinas redondeadas)
    if (this.roundedCorners && this.cornerRadius > 0) {
      const radius = this.cornerRadius;
      const x = borderSize;
      const y = borderSize;
      const w = this.canvasWidth;
      const h = this.canvasHeight;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + w - radius, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
      ctx.lineTo(x + w, y + h - radius);
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
      ctx.lineTo(x + radius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.fill(); // Rellenar con el fondo
      ctx.clip(); // Recortar para las esquinas redondeadas
    } else {
      ctx.fillRect(borderSize, borderSize, this.canvasWidth, this.canvasHeight);
    }

    // Obtener el canvas como imagen primero para asegurar que tiene las dimensiones correctas
    const canvasDataURL = this.canvas.toDataURL({
      format: 'png',
      multiplier: 1,
      quality: 1.0
    });
    
    // Crear una imagen desde el data URL de forma asíncrona
    const canvasImage = new Image();
    canvasImage.onload = () => {
      // Dibujar el canvas original en el centro (con offset si hay borde)
      // Usar las dimensiones actuales del canvas
      ctx.drawImage(
        canvasImage, 
        borderSize, 
        borderSize,
        this.canvasWidth,
        this.canvasHeight
      );

      // Restaurar el contexto si se aplicó clip
      if (this.roundedCorners && this.cornerRadius > 0) {
        ctx.restore();
      }

      // Exportar el canvas temporal con la calidad seleccionada
      const qualityValue = this.jpgQuality / 100; // Convertir porcentaje (0-100) a decimal (0-1)
      const dataURL = tempCanvas.toDataURL('image/jpeg', qualityValue);
      this.downloadImage(dataURL);
    };
    canvasImage.onerror = () => {
      console.error('Error cargando la imagen del canvas');
      alert('Error al exportar el banner. Por favor intenta nuevamente.');
    };
    canvasImage.src = canvasDataURL;
  }

  private downloadImage(dataURL: string) {
    const link = document.createElement('a');
    link.download = `banner_${new Date().getTime()}.jpg`;
    link.href = dataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onRoundedCornersChange() {
    this.updateCanvasBorderRadius();
  }

  onCornerRadiusChange() {
    if (this.roundedCorners) {
      this.updateCanvasBorderRadius();
    }
  }

  onBorderChange() {
    this.updateCanvasBorder();
  }

  onBorderWidthChange() {
    if (this.enableBorder) {
      this.updateCanvasBorder();
    }
  }

  onBorderColorChange() {
    if (this.enableBorder) {
      this.updateCanvasBorder();
    }
  }

  private updateCanvasBorder() {
    const canvasElement = document.getElementById('banner-canvas');
    if (canvasElement) {
      if (this.enableBorder && this.borderWidth > 0) {
        canvasElement.style.border = `${this.borderWidth}px solid ${this.borderColor}`;
      } else {
        canvasElement.style.border = 'none';
      }
    }
  }

  private updateCanvasBorderRadius() {
    const canvasElement = document.getElementById('banner-canvas');
    if (canvasElement) {
      if (this.roundedCorners && this.cornerRadius > 0) {
        canvasElement.style.borderRadius = `${this.cornerRadius}px`;
      } else {
        canvasElement.style.borderRadius = '0';
      }
    }
  }

  clearCanvas() {
    if (this.canvas) {
      this.canvas.clear();
      this.applyBackground();
      this.updateElementsList();
    }
  }

  private limpiarCanvasParaNuevoProyecto() {
    if (!this.canvas) return;
    
    // Limpiar el canvas
    this.canvas.clear();
    
    // Restaurar valores por defecto
    this.canvasWidth = 1000;
    this.canvasHeight = 400;
    this.backgroundColor = '#FFFFFF';
    this.backgroundType = 'solid';
    this.gradientColorStart = '#000000';
    this.gradientColorEnd = '#FF0000';
    this.gradientDirection = 'horizontal';
    this.roundedCorners = false;
    this.cornerRadius = 20;
    this.enableBorder = false;
    this.borderWidth = 5;
    this.borderColor = '#000000';
    this.selectedTemplate = 'personalizado';
    
    // Redimensionar el canvas
    this.canvas.setDimensions({
      width: this.canvasWidth,
      height: this.canvasHeight
    });
    
    // Aplicar fondo
    this.applyBackground();
    this.updateCanvasBorderRadius();
    this.updateCanvasBorder();
    
    // Limpiar lista de elementos
    this.updateElementsList();
    
    // Marcar como guardado (sin cambios pendientes)
    this.markAsSaved();
    
    // Renderizar
    this.canvas.renderAll();
  }

  triggerImageUpload() {
    const fileInput = document.getElementById('image-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  addShape() {
    if (!this.canvas) {
      return;
    }

    let shape: any;
    const centerX = this.canvasWidth / 2;
    const centerY = this.canvasHeight / 2;

    switch (this.selectedShapeType) {
      case 'rect':
        shape = new fabric.Rect({
          left: centerX - 50,
          top: centerY - 50,
          width: 100,
          height: 100,
          fill: this.shapeColor,
          stroke: this.shapeStrokeColor,
          strokeWidth: this.shapeStrokeWidth,
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true
        });
        break;

      case 'circle':
        shape = new fabric.Circle({
          left: centerX,
          top: centerY,
          radius: 50,
          fill: this.shapeColor,
          stroke: this.shapeStrokeColor,
          strokeWidth: this.shapeStrokeWidth,
          originX: 'center',
          originY: 'center',
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true
        });
        break;

      case 'triangle':
        shape = new fabric.Triangle({
          left: centerX,
          top: centerY,
          width: 100,
          height: 100,
          fill: this.shapeColor,
          stroke: this.shapeStrokeColor,
          strokeWidth: this.shapeStrokeWidth,
          originX: 'center',
          originY: 'center',
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true
        });
        break;

      case 'line':
        shape = new fabric.Line([centerX - 50, centerY, centerX + 50, centerY], {
          stroke: this.shapeStrokeColor,
          strokeWidth: this.shapeStrokeWidth,
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true
        });
        break;

      case 'ellipse':
        shape = new fabric.Ellipse({
          left: centerX,
          top: centerY,
          rx: 60,
          ry: 40,
          fill: this.shapeColor,
          stroke: this.shapeStrokeColor,
          strokeWidth: this.shapeStrokeWidth,
          originX: 'center',
          originY: 'center',
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true
        });
        break;

      case 'polygon':
        // Crear un hexágono como ejemplo de polígono
        const points = [];
        const sides = 6;
        const radius = 50;
        for (let i = 0; i < sides; i++) {
          const angle = (Math.PI * 2 * i) / sides;
          points.push({
            x: centerX + radius * Math.cos(angle),
            y: centerY + radius * Math.sin(angle)
          });
        }
        shape = new fabric.Polygon(points, {
          fill: this.shapeColor,
          stroke: this.shapeStrokeColor,
          strokeWidth: this.shapeStrokeWidth,
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true
        });
        break;

      default:
        return;
    }

    this.canvas.add(shape);
    this.canvas.setActiveObject(shape);
    this.canvas.renderAll();
    this.updateElementsList();
  }

  trackByElementId(index: number, element: CanvasElement): string {
    return element.id;
  }

  isElementActive(element: CanvasElement): boolean {
    if (!this.canvas || !element.object) {
      return false;
    }
    const activeObject = this.canvas.getActiveObject();
    return activeObject === element.object;
  }

  toggleExportMenu() {
    this.showExportMenu = !this.showExportMenu;
    if (this.showExportMenu) {
      this.showPngOptions = false;
    }
  }
  
  // Métodos para el menú lateral estilo VS Code
  togglePanel(panel: 'config' | 'content' | 'elements') {
    if (this.activePanel === panel) {
      this.activePanel = null; // Cerrar si ya está abierto
    } else {
      this.activePanel = panel; // Abrir el panel seleccionado
    }
  }
  
  closePanel() {
    this.activePanel = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.export-menu-container')) {
      this.showExportMenu = false;
      this.showPngOptions = false;
    }
    if (!target.closest('.custom-template-select')) {
      this.showTemplateDropdown = false;
    }
    if (!target.closest('.drawer') && !target.closest('.main-menu-button')) {
      // No cerrar el drawer aquí, se maneja con el overlay
    }
  }

  exportPng(backgroundType: 'color' | 'transparent') {
    if (!this.canvas) {
      return;
    }

    this.showExportMenu = false;
    this.showPngOptions = false;

    // Calcular dimensiones del canvas final (incluyendo borde si está habilitado)
    const borderSize = this.enableBorder ? this.borderWidth : 0;
    const finalWidth = this.canvasWidth + (borderSize * 2);
    const finalHeight = this.canvasHeight + (borderSize * 2);

    // Crear canvas temporal para la exportación
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = finalWidth;
    tempCanvas.height = finalHeight;
    const ctx = tempCanvas.getContext('2d');

    if (!ctx) {
      return;
    }

    // Si el fondo es transparente, no dibujar nada de fondo
    // Si el fondo es de color, dibujar el fondo
    if (backgroundType === 'color') {
      // Si hay borde, dibujarlo primero como fondo
      if (this.enableBorder && this.borderWidth > 0) {
        if (this.roundedCorners && this.cornerRadius > 0) {
          // Borde con esquinas redondeadas
          const radius = this.cornerRadius + borderSize;
          ctx.fillStyle = this.borderColor;
          ctx.beginPath();
          ctx.moveTo(radius, 0);
          ctx.lineTo(finalWidth - radius, 0);
          ctx.quadraticCurveTo(finalWidth, 0, finalWidth, radius);
          ctx.lineTo(finalWidth, finalHeight - radius);
          ctx.quadraticCurveTo(finalWidth, finalHeight, finalWidth - radius, finalHeight);
          ctx.lineTo(radius, finalHeight);
          ctx.quadraticCurveTo(0, finalHeight, 0, finalHeight - radius);
          ctx.lineTo(0, radius);
          ctx.quadraticCurveTo(0, 0, radius, 0);
          ctx.closePath();
          ctx.fill();
        } else {
          // Borde recto
          ctx.fillStyle = this.borderColor;
          ctx.fillRect(0, 0, finalWidth, finalHeight);
        }
      }

      // Dibujar el fondo del canvas
      if (this.backgroundType === 'solid') {
        ctx.fillStyle = this.backgroundColor;
      } else {
        // Para degradado, necesitamos crear un gradiente
        const gradient = ctx.createLinearGradient(
          borderSize, borderSize,
          borderSize + this.canvasWidth, borderSize + this.canvasHeight
        );
        gradient.addColorStop(0, this.gradientColorStart);
        gradient.addColorStop(1, this.gradientColorEnd);
        ctx.fillStyle = gradient;
      }

      // Crear área de recorte para el contenido del canvas
      if (this.roundedCorners && this.cornerRadius > 0) {
        const radius = this.cornerRadius;
        const x = borderSize;
        const y = borderSize;
        const w = this.canvasWidth;
        const h = this.canvasHeight;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + w - radius, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
        ctx.lineTo(x + w, y + h - radius);
        ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
        ctx.lineTo(x + radius, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        ctx.fill();
        ctx.clip();
      } else {
        ctx.fillRect(borderSize, borderSize, this.canvasWidth, this.canvasHeight);
      }
    } else {
      // Fondo transparente - solo dibujar borde si está habilitado
      if (this.enableBorder && this.borderWidth > 0) {
        if (this.roundedCorners && this.cornerRadius > 0) {
          const radius = this.cornerRadius + borderSize;
          ctx.strokeStyle = this.borderColor;
          ctx.lineWidth = this.borderWidth * 2;
          ctx.beginPath();
          ctx.moveTo(radius, borderSize);
          ctx.lineTo(finalWidth - radius, borderSize);
          ctx.quadraticCurveTo(finalWidth - borderSize, borderSize, finalWidth - borderSize, radius);
          ctx.lineTo(finalWidth - borderSize, finalHeight - radius);
          ctx.quadraticCurveTo(finalWidth - borderSize, finalHeight - borderSize, finalWidth - radius, finalHeight - borderSize);
          ctx.lineTo(radius, finalHeight - borderSize);
          ctx.quadraticCurveTo(borderSize, finalHeight - borderSize, borderSize, finalHeight - radius);
          ctx.lineTo(borderSize, radius);
          ctx.quadraticCurveTo(borderSize, borderSize, radius, borderSize);
          ctx.closePath();
          ctx.stroke();
        } else {
          ctx.strokeStyle = this.borderColor;
          ctx.lineWidth = this.borderWidth * 2;
          ctx.strokeRect(borderSize, borderSize, this.canvasWidth, this.canvasHeight);
        }
      }
    }

    // Aplicar clipPath para esquinas redondeadas si el fondo es transparente
    if (backgroundType === 'transparent' && this.roundedCorners && this.cornerRadius > 0) {
      const radius = this.cornerRadius;
      const x = borderSize;
      const y = borderSize;
      const w = this.canvasWidth;
      const h = this.canvasHeight;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + w - radius, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
      ctx.lineTo(x + w, y + h - radius);
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
      ctx.lineTo(x + radius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.clip();
    }

    // Obtener el canvas como imagen primero para asegurar que tiene las dimensiones correctas
    const canvasDataURL = this.canvas.toDataURL({
      format: 'png',
      multiplier: 1,
      quality: 1.0
    });
    
    // Crear una imagen desde el data URL
    const canvasImage = new Image();
    canvasImage.onload = () => {
      // Dibujar el canvas original en el centro (con offset si hay borde)
      // Usar las dimensiones actuales del canvas
      ctx.drawImage(
        canvasImage, 
        borderSize, 
        borderSize,
        this.canvasWidth,
        this.canvasHeight
      );

      // Restaurar el contexto si se aplicó clip
      if ((backgroundType === 'color' && this.roundedCorners && this.cornerRadius > 0) ||
          (backgroundType === 'transparent' && this.roundedCorners && this.cornerRadius > 0)) {
        ctx.restore();
      }

      // Exportar como PNG
      const dataURL = tempCanvas.toDataURL('image/png');
      this.downloadFile(dataURL, 'png');
    };
    canvasImage.src = canvasDataURL;
  }

  exportPdf() {
    if (!this.canvas) {
      return;
    }

    this.showExportMenu = false;

    // Verificar si jsPDF está disponible
    if (typeof jsPDF === 'undefined') {
      alert('La librería jsPDF no está disponible. Por favor, ejecuta: npm install jspdf');
      return;
    }

    // Obtener la imagen del canvas como data URL
    const dataURL = this.canvas.toDataURL('image/png', 1.0);

    // Crear un nuevo documento PDF
    const pdf = new jsPDF({
      orientation: this.canvasWidth > this.canvasHeight ? 'landscape' : 'portrait',
      unit: 'px',
      format: [this.canvasWidth, this.canvasHeight]
    });

    // Agregar la imagen al PDF
    pdf.addImage(dataURL, 'PNG', 0, 0, this.canvasWidth, this.canvasHeight, undefined, 'FAST');

    // Descargar el PDF
    pdf.save(`banner_${new Date().getTime()}.pdf`);
  }

  private downloadFile(dataURL: string, format: 'jpg' | 'png') {
    const link = document.createElement('a');
    link.download = `banner_${new Date().getTime()}.${format}`;
    link.href = dataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Métodos para proyectos
  private cargarProyecto() {
    if (!this.proyectoId) return;

    this.proyectosService.getProyecto(this.proyectoId).subscribe({
      next: (proyecto) => {
        if (proyecto && proyecto.DatosProyecto) {
          const datos = typeof proyecto.DatosProyecto === 'string' 
            ? JSON.parse(proyecto.DatosProyecto) 
            : proyecto.DatosProyecto;

          // Restaurar configuración del canvas
          if (datos.canvasWidth) this.canvasWidth = datos.canvasWidth;
          if (datos.canvasHeight) this.canvasHeight = datos.canvasHeight;
          if (datos.backgroundColor) this.backgroundColor = datos.backgroundColor;
          if (datos.backgroundType) this.backgroundType = datos.backgroundType;
          if (datos.gradientColorStart) this.gradientColorStart = datos.gradientColorStart;
          if (datos.gradientColorEnd) this.gradientColorEnd = datos.gradientColorEnd;
          if (datos.gradientDirection) this.gradientDirection = datos.gradientDirection;
          if (datos.roundedCorners !== undefined) this.roundedCorners = datos.roundedCorners;
          if (datos.cornerRadius) this.cornerRadius = datos.cornerRadius;
          if (datos.enableBorder !== undefined) this.enableBorder = datos.enableBorder;
          if (datos.borderWidth) this.borderWidth = datos.borderWidth;
          if (datos.borderColor) this.borderColor = datos.borderColor;
          if (datos.jpgQuality) this.jpgQuality = datos.jpgQuality;

          // Esperar a que el canvas esté inicializado antes de cargar elementos
          setTimeout(() => {
            if (this.canvas) {
              // Redimensionar canvas si es necesario
              if (datos.canvasWidth && datos.canvasHeight) {
                this.canvas.setWidth(datos.canvasWidth);
                this.canvas.setHeight(datos.canvasHeight);
              }
              
              if (datos.elements && Array.isArray(datos.elements)) {
                this.cargarElementos(datos.elements);
              }
            }
          }, 500);
        }
      },
      error: (error) => {
        console.error('Error cargando proyecto:', error);
      }
    });
  }

  private cargarElementos(elements: any[]) {
    if (!this.canvas) return;

    // Limpiar canvas primero
    this.canvas.clear();
    this.canvas.backgroundColor = this.backgroundColor;
    this.applyBackground();
    this.updateCanvasBorderRadius();
    this.updateCanvasBorder();

    // Cargar cada elemento
    const loadPromises = elements.map((elementData: any) => {
      return new Promise((resolve) => {
        if (elementData.type === 'text') {
          const text = new fabric.Text(elementData.text, {
            left: elementData.left,
            top: elementData.top,
            fontSize: elementData.fontSize,
            fontFamily: elementData.fontFamily,
            fill: elementData.fill,
            fontWeight: elementData.fontWeight || 'normal',
            fontStyle: elementData.fontStyle || 'normal',
            underline: elementData.underline || false,
            angle: elementData.angle || 0,
            scaleX: elementData.scaleX || 1,
            scaleY: elementData.scaleY || 1,
            selectable: true,
            evented: true,
            hasControls: true,
            hasBorders: true
          });
          
          this.canvas.add(text);
          resolve(text);
        } else if (elementData.type === 'image') {
          fabric.Image.fromURL(elementData.src, (img: any) => {
            // Guardar la URL del storage si es una URL HTTP/HTTPS (no Base64)
            if (elementData.src && (elementData.src.startsWith('http://') || elementData.src.startsWith('https://'))) {
              img.set('storageUrl', elementData.src);
            }
            
            img.set({
              left: elementData.left,
              top: elementData.top,
              scaleX: elementData.scaleX || 1,
              scaleY: elementData.scaleY || 1,
              angle: elementData.angle || 0,
              selectable: true,
              evented: true,
              hasControls: true,
              hasBorders: true
            });
            this.canvas.add(img);
            resolve(img);
          }, { crossOrigin: 'anonymous' });
        } else if (elementData.type === 'shape') {
          let shape: any;
          switch (elementData.shapeType) {
            case 'rect':
              shape = new fabric.Rect(elementData);
              break;
            case 'circle':
              shape = new fabric.Circle(elementData);
              break;
            case 'triangle':
              shape = new fabric.Triangle(elementData);
              break;
            case 'line':
              shape = new fabric.Line(elementData.points, elementData);
              break;
            case 'ellipse':
              shape = new fabric.Ellipse(elementData);
              break;
          }
          if (shape) {
            shape.set({
              selectable: true,
              evented: true,
              hasControls: true,
              hasBorders: true
            });
            this.canvas.add(shape);
            resolve(shape);
          }
        }
      });
    });

    Promise.all(loadPromises).then(() => {
      this.canvas.renderAll();
      this.updateElementsList();
      // Marcar como guardado después de cargar
      this.markAsSaved();
    });
  }

  // Método privado eliminado - ya no se usa guardado automático


  private ejecutarGuardado() {
    if (!this.proyectoId || !this.canvas) {
      return;
    }

    // Serializar elementos del canvas
    const elements = this.canvas.getObjects().map((obj: any) => {
      const baseData: any = {
        type: obj.type,
        left: obj.left,
        top: obj.top,
        angle: obj.angle || 0,
        scaleX: obj.scaleX || 1,
        scaleY: obj.scaleY || 1
      };

      if (obj.type === 'text') {
        const textData: any = {
          ...baseData,
          type: 'text',
          text: obj.text,
          fontSize: obj.fontSize,
          fontFamily: obj.fontFamily,
          fill: obj.fill,
          fontWeight: obj.fontWeight || 'normal',
          fontStyle: obj.fontStyle || 'normal',
          underline: obj.underline || false
        };
        
        // Agregar color de fondo si existe y no es transparente (Fabric.js usa textBackgroundColor)
        const bgColor = obj.textBackgroundColor || obj.backgroundColor;
        if (bgColor && bgColor !== 'transparent' && bgColor !== '') {
          textData.backgroundColor = bgColor; // Guardamos como backgroundColor para compatibilidad
        }
        
        // Agregar borde si existe y tiene grosor mayor a 0
        if (obj.strokeWidth && obj.strokeWidth > 0) {
          textData.stroke = obj.stroke;
          textData.strokeWidth = obj.strokeWidth;
        }
        
        return textData;
      } else if (obj.type === 'image') {
        // Priorizar URL del storage si existe - NUNCA usar base64 si hay URL del storage
        let imageSrc = '';
        
        // Primero intentar obtener storageUrl de las propiedades del objeto
        try {
          if (obj.get && typeof obj.get === 'function') {
            const storageUrl = obj.get('storageUrl');
            if (storageUrl && typeof storageUrl === 'string' && storageUrl.trim() !== '') {
              imageSrc = storageUrl;
            }
          }
        } catch (e) {
          console.warn('Error obteniendo storageUrl con get():', e);
        }
        
        // Si no se encontró con get(), intentar directamente desde la propiedad
        if (!imageSrc && obj.storageUrl && typeof obj.storageUrl === 'string' && obj.storageUrl.trim() !== '') {
          imageSrc = obj.storageUrl;
        }
        
        // Si aún no hay URL del storage, verificar si el src actual es una URL HTTP/HTTPS
        if (!imageSrc) {
          try {
            if (typeof obj.getSrc === 'function') {
              const src = obj.getSrc();
              // Si es una URL HTTP/HTTPS (del storage o pública), usarla directamente
              if (src && typeof src === 'string' && (src.startsWith('http://') || src.startsWith('https://'))) {
                imageSrc = src;
              }
            } else if (obj.src && typeof obj.src === 'string' && (obj.src.startsWith('http://') || obj.src.startsWith('https://'))) {
              imageSrc = obj.src;
            }
          } catch (e) {
            console.warn('Error obteniendo src de imagen:', e);
          }
        }
        
        // Si NO hay URL del storage, NO guardar base64 - dejar vacío o mostrar advertencia
        // Esto evita guardar imágenes grandes como base64
        if (!imageSrc) {
          console.warn('Advertencia: Imagen sin URL del storage. La imagen no se guardará correctamente.');
          // Opcional: podrías subir la imagen al storage aquí si no tiene URL
          // Por ahora, dejamos vacío para que el usuario sepa que hay un problema
          imageSrc = '';
        }
        
        return {
          ...baseData,
          type: 'image',
          src: imageSrc
        };
      } else if (obj.type === 'rect' || obj.type === 'circle' || obj.type === 'triangle' || obj.type === 'ellipse') {
        return {
          ...baseData,
          type: 'shape',
          shapeType: obj.type,
          width: obj.width,
          height: obj.height,
          fill: obj.fill,
          stroke: obj.stroke,
          strokeWidth: obj.strokeWidth
        };
      } else if (obj.type === 'line') {
        return {
          ...baseData,
          type: 'shape',
          shapeType: 'line',
          points: obj.points,
          stroke: obj.stroke,
          strokeWidth: obj.strokeWidth
        };
      }
      return null;
    }).filter((el: any) => el !== null);

    // Crear objeto de datos del proyecto
    const datosProyecto = {
      canvasWidth: this.canvasWidth,
      canvasHeight: this.canvasHeight,
      backgroundColor: this.backgroundColor,
      backgroundType: this.backgroundType,
      gradientColorStart: this.gradientColorStart,
      gradientColorEnd: this.gradientColorEnd,
      gradientDirection: this.gradientDirection,
      roundedCorners: this.roundedCorners,
      cornerRadius: this.cornerRadius,
      enableBorder: this.enableBorder,
      borderWidth: this.borderWidth,
      borderColor: this.borderColor,
      jpgQuality: this.jpgQuality,
      elements: elements
    };
    
    // Validar tamaño del payload antes de enviar
    const datosJson = JSON.stringify(datosProyecto);
    const payloadSizeMB = new Blob([datosJson]).size / (1024 * 1024);
    console.log(`Tamaño del payload: ${payloadSizeMB.toFixed(2)}MB`);
    
    // Si el payload es muy grande (>4MB), mostrar advertencia
    if (payloadSizeMB > 4) {
      console.warn(`Payload grande: ${payloadSizeMB.toFixed(2)}MB. Esto puede causar problemas de conexión.`);
      // Intentar comprimir más las imágenes que aún sean grandes
      const compressedElements = elements.map((el: any) => {
        if (el.type === 'image' && el.src && el.src.startsWith('data:image') && el.src.length > 1000000) {
          // Si la imagen es mayor a 1MB, intentar comprimirla más
          const img = new Image();
          img.crossOrigin = 'anonymous';
          try {
            img.src = el.src;
            if (img.complete) {
              el.src = this.compressImageForStorage(img, 800, 0.55);
            }
          } catch (e) {
            console.warn('No se pudo re-comprimir imagen:', e);
          }
        }
        return el;
      });
      datosProyecto.elements = compressedElements;
      
      // Validar tamaño final
      const finalJson = JSON.stringify(datosProyecto);
      const finalSizeMB = new Blob([finalJson]).size / (1024 * 1024);
      console.log(`Tamaño final después de compresión: ${finalSizeMB.toFixed(2)}MB`);
      
      if (finalSizeMB > 5) {
        alert(`Advertencia: El proyecto es muy grande (${finalSizeMB.toFixed(2)}MB). Esto puede causar problemas al guardar. Considera usar imágenes más pequeñas.`);
      }
    }

    // Generar miniatura optimizada (más pequeña y comprimida)
    // Primero reducir el tamaño del canvas para la miniatura
    const miniaturaSize = 200; // Tamaño máximo de la miniatura
    const scale = Math.min(miniaturaSize / this.canvasWidth, miniaturaSize / this.canvasHeight);
    const thumbnailWidth = Math.floor(this.canvasWidth * scale);
    const thumbnailHeight = Math.floor(this.canvasHeight * scale);
    
    // Crear un canvas temporal más pequeño para la miniatura
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = thumbnailWidth;
    tempCanvas.height = thumbnailHeight;
    const tempCtx = tempCanvas.getContext('2d');
    
    let miniatura: string | undefined;
    try {
      if (tempCtx) {
        // Escalar el canvas original al tamaño de miniatura
        tempCtx.drawImage(
          this.canvas.getElement(),
          0, 0, this.canvasWidth, this.canvasHeight,
          0, 0, thumbnailWidth, thumbnailHeight
        );
        // Generar miniatura con calidad muy baja y tamaño pequeño
        miniatura = tempCanvas.toDataURL('image/jpeg', 0.2);
      }
    } catch (e) {
      console.warn('No se pudo generar miniatura:', e);
      // Continuar sin miniatura si falla
    }

    // Guardar primero solo los datos del proyecto (más críticos)
    // La miniatura se puede actualizar después si es necesario
    this.proyectosService.updateProyecto(this.proyectoId, {
      datosProyecto: datosProyecto,
      miniatura: miniatura // Opcional, si falla se puede omitir
    }).subscribe({
      next: () => {
        console.log('Proyecto guardado correctamente');
        this.mostrarNotificacionGuardado();
        this.markAsSaved();
      },
      error: (error) => {
        console.error('Error guardando proyecto:', error);
        
        // Intentar guardar sin miniatura si el error persiste
        if (error.error?.details?.includes('ECONNRESET') || error.error?.details?.includes('Connection lost')) {
          console.log('Reintentando guardado sin miniatura...');
          this.proyectosService.updateProyecto(this.proyectoId, {
            datosProyecto: datosProyecto
            // Sin miniatura esta vez
          }).subscribe({
            next: () => {
              console.log('Proyecto guardado correctamente (sin miniatura)');
              this.mostrarNotificacionGuardado();
              this.markAsSaved();
            },
            error: (retryError) => {
              console.error('Error guardando proyecto (reintento):', retryError);
              alert('Error al guardar el proyecto. Los cambios se guardarán localmente. Por favor intenta guardar nuevamente más tarde.');
            }
          });
        } else {
          alert('Error al guardar el proyecto. Por favor intenta nuevamente.');
        }
      }
    });
  }

  // Método para guardar cuando cambian propiedades del canvas (sin guardado automático)
  onCanvasPropertyChange() {
    if (this.canvas) {
      this.applyBackground();
      this.updateCanvasBorderRadius();
      this.updateCanvasBorder();
      // Ya no guardamos automáticamente
    }
  }

  // Método público para guardar manualmente
  guardarProyectoManual() {
    if (!this.proyectoId) {
      alert('No hay un proyecto activo para guardar. Crea un nuevo proyecto desde el menú lateral.');
      return;
    }
    this.ejecutarGuardado();
  }

  // Mostrar notificación de guardado exitoso
  mostrarNotificacionGuardado() {
    // Limpiar timeout anterior si existe
    if (this.saveNotificationTimeout) {
      clearTimeout(this.saveNotificationTimeout);
    }
    
    // Mostrar notificación
    this.showSaveNotification = true;
    
    // Ocultar después de 3 segundos
    this.saveNotificationTimeout = setTimeout(() => {
      this.showSaveNotification = false;
    }, 3000);
  }

  // Métodos para drag and drop de capas
  onDragStart(event: DragEvent, index: number) {
    this.draggedElementIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', index.toString());
    }
    event.stopPropagation();
  }

  onDragOver(event: DragEvent, index: number) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    if (this.draggedElementIndex !== null && this.draggedElementIndex !== index) {
      this.dragOverIndex = index;
    }
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent) {
    this.dragOverIndex = null;
    event.stopPropagation();
  }

  onDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    event.stopPropagation();

    if (this.draggedElementIndex === null || this.draggedElementIndex === dropIndex) {
      this.dragOverIndex = null;
      this.draggedElementIndex = null;
      return;
    }

    // Reordenar elementos en el array
    const draggedElement = this.canvasElements[this.draggedElementIndex];
    const newElements = [...this.canvasElements];
    newElements.splice(this.draggedElementIndex, 1);
    newElements.splice(dropIndex, 0, draggedElement);

    // Actualizar el orden en el canvas
    this.reordenarCapasEnCanvas(newElements);

    // Actualizar la lista
    this.canvasElements = newElements;
    
    this.dragOverIndex = null;
    this.draggedElementIndex = null;
    this.cdr.detectChanges();
  }

  onDragEnd(event: DragEvent) {
    this.dragOverIndex = null;
    this.draggedElementIndex = null;
    event.stopPropagation();
  }

  // Función auxiliar para comprimir una imagen de manera agresiva
  private compressImageForStorage(imgElement: HTMLImageElement, maxDimension: number = 1024, quality: number = 0.65): string {
    try {
      const canvas = document.createElement('canvas');
      let width = imgElement.naturalWidth || imgElement.width || 100;
      let height = imgElement.naturalHeight || imgElement.height || 100;
      
      // Redimensionar si es muy grande
      if (width > maxDimension || height > maxDimension) {
        const scale = Math.min(maxDimension / width, maxDimension / height);
        width = Math.floor(width * scale);
        height = Math.floor(height * scale);
      }
      
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        return '';
      }
      
      // Configurar calidad de renderizado
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'medium';
      
      // Dibujar la imagen
      ctx.drawImage(imgElement, 0, 0, width, height);
      
      // Convertir a JPEG con calidad reducida
      let dataURL = canvas.toDataURL('image/jpeg', quality);
      
      // Si aún es muy grande (>1.5MB), reducir calidad más
      if (dataURL.length > 1500000) {
        dataURL = canvas.toDataURL('image/jpeg', Math.max(0.45, quality - 0.1));
      }
      if (dataURL.length > 1500000) {
        dataURL = canvas.toDataURL('image/jpeg', Math.max(0.35, quality - 0.2));
      }
      
      return dataURL;
    } catch (e) {
      console.error('Error comprimiendo imagen:', e);
      return '';
    }
  }

  private reordenarCapasEnCanvas(newOrder: CanvasElement[]) {
    if (!this.canvas) return;

    // Limpiar todos los objetos del canvas temporalmente
    const objects = this.canvas.getObjects();
    
    // Reordenar según el nuevo orden de la lista
    // El orden en la lista es de abajo a arriba (el último en la lista está arriba)
    // Pero en fabric.js, el orden en el array de objetos es de atrás a adelante
    // Así que necesitamos invertir el orden para que el último de la lista quede al frente
    
    // Remover todos los objetos
    this.canvas.remove(...objects);
    
    // Agregar los objetos en el nuevo orden (de abajo hacia arriba)
    newOrder.forEach((element) => {
      if (element.object) {
        this.canvas.add(element.object);
      }
    });

    // Renderizar
    this.canvas.renderAll();
  }

  // Métodos para el panel de propiedades
  openElementPropertiesModal(obj: any) {
    if (!obj || !this.canvas) return;
    
    this.selectedElementForEdit = obj;
    this.canvas.setActiveObject(obj);
    this.canvas.renderAll();
    
    // Cargar propiedades según el tipo de elemento
    this.elementProperties = {
      left: Math.round(obj.left || 0),
      top: Math.round(obj.top || 0),
      angle: Math.round(obj.angle || 0),
      scaleX: Math.round((obj.scaleX || 1) * 100) / 100,
      scaleY: Math.round((obj.scaleY || 1) * 100) / 100,
      opacity: Math.round((obj.opacity || 1) * 100) / 100
    };

    // Propiedades específicas según el tipo
    if (obj instanceof fabric.Text) {
      this.elementProperties.text = obj.text || '';
      this.elementProperties.fontSize = obj.fontSize || 24;
      this.elementProperties.fontFamily = obj.fontFamily || 'Arial';
      this.elementProperties.fill = obj.fill || '#000000';
      this.elementProperties.fontWeight = obj.fontWeight || 'normal';
      this.elementProperties.fontStyle = obj.fontStyle || 'normal';
      this.elementProperties.underline = obj.underline || false;
    } else if (obj instanceof fabric.Image) {
      // Las imágenes no tienen muchas propiedades editables además de posición y escala
      // Calcular dimensiones visibles de la imagen
      const scaleX = obj.scaleX || 1;
      const scaleY = obj.scaleY || 1;
      this.elementProperties.width = Math.round((obj.width || 0) * scaleX);
      this.elementProperties.height = Math.round((obj.height || 0) * scaleY);
    } else if (obj instanceof fabric.Rect || 
               obj instanceof fabric.Circle || 
               obj instanceof fabric.Triangle ||
               obj instanceof fabric.Ellipse ||
               obj instanceof fabric.Polygon) {
      this.elementProperties.fill = obj.fill || '#FF0000';
      this.elementProperties.stroke = obj.stroke || '#000000';
      this.elementProperties.strokeWidth = obj.strokeWidth || 2;
      if (obj instanceof fabric.Rect) {
        // Mostrar dimensiones escaladas visibles
        this.elementProperties.width = Math.round((obj.width || 100) * (obj.scaleX || 1));
        this.elementProperties.height = Math.round((obj.height || 100) * (obj.scaleY || 1));
      } else if (obj instanceof fabric.Circle) {
        // Mostrar radio escalado visible
        this.elementProperties.radius = Math.round((obj.radius || 50) * (obj.scaleX || 1));
      }
    } else if (obj instanceof fabric.Line) {
      this.elementProperties.stroke = obj.stroke || '#000000';
      this.elementProperties.strokeWidth = obj.strokeWidth || 2;
    }

    // Abrir el panel de propiedades en lugar del modal
    this.activePanel = 'properties';
  }

  closeElementPropertiesModal() {
    this.activePanel = null;
    this.selectedElementForEdit = null;
  }

  applyElementProperties() {
    if (!this.selectedElementForEdit || !this.canvas) return;

    const obj = this.selectedElementForEdit;

    // Aplicar propiedades generales
    obj.set({
      left: this.elementProperties.left,
      top: this.elementProperties.top,
      angle: this.elementProperties.angle,
      scaleX: this.elementProperties.scaleX,
      scaleY: this.elementProperties.scaleY,
      opacity: this.elementProperties.opacity
    });

    // Aplicar propiedades específicas según el tipo
    if (obj instanceof fabric.Text) {
      obj.set({
        text: this.elementProperties.text,
        fontSize: this.elementProperties.fontSize,
        fontFamily: this.elementProperties.fontFamily,
        fill: this.elementProperties.fill,
        fontWeight: this.elementProperties.fontWeight || 'normal',
        fontStyle: this.elementProperties.fontStyle || 'normal',
        underline: this.elementProperties.underline || false
      });
    } else if (obj instanceof fabric.Rect || 
               obj instanceof fabric.Circle || 
               obj instanceof fabric.Triangle ||
               obj instanceof fabric.Ellipse ||
               obj instanceof fabric.Polygon) {
      obj.set({
        fill: this.elementProperties.fill,
        stroke: this.elementProperties.stroke,
        strokeWidth: this.elementProperties.strokeWidth
      });
      
      // Para rectángulos, ajustar width y height
      if (obj instanceof fabric.Rect && this.elementProperties.width && this.elementProperties.height) {
        // Ajustar el width y height base dividiendo por la nueva escala
        obj.set({
          width: this.elementProperties.width / this.elementProperties.scaleX,
          height: this.elementProperties.height / this.elementProperties.scaleY
        });
      }
      
      // Para círculos, ajustar radius
      if (obj instanceof fabric.Circle && this.elementProperties.radius) {
        // Ajustar el radius base dividiendo por la nueva escala
        obj.set({
          radius: this.elementProperties.radius / this.elementProperties.scaleX
        });
      }
    } else if (obj instanceof fabric.Line) {
      obj.set({
        stroke: this.elementProperties.stroke,
        strokeWidth: this.elementProperties.strokeWidth
      });
    }

    this.canvas.renderAll();
    this.updateElementsList();
    this.markAsChanged();
    // No cerrar el panel, mantenerlo abierto para seguir editando
  }

  // Aplicar propiedades automáticamente cuando cambian (sin botón de aplicar)
  onElementPropertyChange() {
    if (this.selectedElementForEdit) {
      this.applyElementProperties();
    }
  }

  getElementTypeName(): string {
    if (!this.selectedElementForEdit) return '';
    
    if (this.selectedElementForEdit instanceof fabric.Text) {
      return 'Texto';
    } else if (this.selectedElementForEdit instanceof fabric.Image) {
      return 'Imagen';
    } else if (this.selectedElementForEdit instanceof fabric.Rect) {
      return 'Rectángulo';
    } else if (this.selectedElementForEdit instanceof fabric.Circle) {
      return 'Círculo';
    } else if (this.selectedElementForEdit instanceof fabric.Triangle) {
      return 'Triángulo';
    } else if (this.selectedElementForEdit instanceof fabric.Line) {
      return 'Línea';
    } else if (this.selectedElementForEdit instanceof fabric.Ellipse) {
      return 'Elipse';
    } else if (this.selectedElementForEdit instanceof fabric.Polygon) {
      return 'Polígono';
    }
    return 'Elemento';
  }

  isTextElement(): boolean {
    if (!this.selectedElementForEdit) return false;
    
    // Detectar si es un grupo con texto o texto simple
    if (this.selectedElementForEdit instanceof fabric.Text) {
      return true;
    }
    
    if (this.selectedElementForEdit instanceof fabric.Group) {
      const objects = this.selectedElementForEdit.getObjects();
      return objects.some((o: any) => o instanceof fabric.Text);
    }
    
    return false;
  }

  isImageElement(): boolean {
    return this.selectedElementForEdit instanceof fabric.Image;
  }

  // FUNCIONALIDAD DE ELIMINAR FONDO TEMPORALMENTE DESHABILITADA
  async removeImageBackground() {
    // Mostrar mensaje informativo
    alert('Funcionalidad próximamente');
    return;
    
    // CÓDIGO COMENTADO - FUNCIONALIDAD DE ELIMINAR FONDO
    // if (!this.selectedElementForEdit || !(this.selectedElementForEdit instanceof fabric.Image) || !this.canvas) {
    //   return;
    // }

    // const imgObj = this.selectedElementForEdit;
    // const imageUrl = imgObj.get('storageUrl') || imgObj.getSrc();

    // if (!imageUrl) {
    //   alert('No se pudo obtener la URL de la imagen');
    //   return;
    // }

    // this.isRemovingBackground = true;
    // this.backgroundRemovalProgress = 0;

    // try {
    //   // Simular progreso inicial
    //   this.backgroundRemovalProgress = 10;

    //   // Descargar la imagen original
    //   const response = await fetch(imageUrl);
    //   if (!response.ok) {
    //     throw new Error('Error al descargar la imagen');
    //   }

    //   this.backgroundRemovalProgress = 30;

    //   const blob = await response.blob();
    //   const file = new File([blob], 'image.jpg', { type: blob.type || 'image/jpeg' });

    //   this.backgroundRemovalProgress = 50;

    //   // Llamar al servicio para eliminar el fondo
    //   this.proyectosService.removeBackground(file, 'birefnet-massive').subscribe({
    //     next: (result) => {
    //       this.backgroundRemovalProgress = 80;

    //       // Cargar la imagen procesada
    //       fabric.Image.fromURL(result.processed_url, (processedImg: any) => {
    //         if (!processedImg || !processedImg.width || !processedImg.height) {
    //           throw new Error('Error al cargar la imagen procesada');
    //         }

    //         // Mantener las propiedades de la imagen original
    //         const originalLeft = imgObj.left;
    //         const originalTop = imgObj.top;
    //         const originalScaleX = imgObj.scaleX || 1;
    //         const originalScaleY = imgObj.scaleY || 1;
    //         const originalAngle = imgObj.angle || 0;
    //         const originalOpacity = imgObj.opacity || 1;

    //         // Aplicar las mismas transformaciones a la nueva imagen
    //         processedImg.set({
    //           left: originalLeft,
    //           top: originalTop,
    //           scaleX: originalScaleX,
    //           scaleY: originalScaleY,
    //           angle: originalAngle,
    //           opacity: originalOpacity,
    //           selectable: true,
    //           evented: true,
    //           hasControls: true,
    //           hasBorders: true,
    //           storageUrl: result.processed_url
    //         });

    //         // Reemplazar la imagen en el canvas
    //         const objects = this.canvas.getObjects();
    //         const index = objects.indexOf(imgObj);
            
    //         this.canvas.remove(imgObj);
    //         this.canvas.insertAt(processedImg, index);
    //         this.canvas.setActiveObject(processedImg);
    //         this.canvas.renderAll();

    //         // Actualizar el elemento seleccionado
    //         this.selectedElementForEdit = processedImg;
    //         this.updateElementsList();
    //         this.markAsChanged();

    //         this.backgroundRemovalProgress = 100;
    //         this.isRemovingBackground = false;

    //         // Ocultar la barra de progreso después de un momento
    //         setTimeout(() => {
    //           this.backgroundRemovalProgress = 0;
    //         }, 1000);
    //       }, { crossOrigin: 'anonymous' });
    //     },
    //     error: (error) => {
    //       console.error('Error eliminando fondo:', error);
    //       alert('Error al eliminar el fondo de la imagen. Por favor, intenta nuevamente.');
    //       this.isRemovingBackground = false;
    //       this.backgroundRemovalProgress = 0;
    //     }
    //   });
    // } catch (error) {
    //   console.error('Error en removeImageBackground:', error);
    //   alert('Error al procesar la imagen. Por favor, intenta nuevamente.');
    //   this.isRemovingBackground = false;
    //   this.backgroundRemovalProgress = 0;
    // }
  }

  isShapeElement(): boolean {
    return this.selectedElementForEdit instanceof fabric.Rect ||
           this.selectedElementForEdit instanceof fabric.Circle ||
           this.selectedElementForEdit instanceof fabric.Triangle ||
           this.selectedElementForEdit instanceof fabric.Ellipse ||
           this.selectedElementForEdit instanceof fabric.Polygon ||
           this.selectedElementForEdit instanceof fabric.Line;
  }

  isRectangleElement(): boolean {
    return this.selectedElementForEdit instanceof fabric.Rect;
  }

  isCircleElement(): boolean {
    return this.selectedElementForEdit instanceof fabric.Circle;
  }

  // Métodos para guardar como proyecto
  openSaveProjectModal() {
    // Generar nombre sugerido basado en fecha y hora
    const now = new Date();
    const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
    this.projectName = `banner_${timestamp}`;
    this.showSaveProjectModal = true;
  }

  closeSaveProjectModal() {
    this.showSaveProjectModal = false;
    this.projectName = '';
    this.projectColor = '#3b82f6'; // Resetear a color por defecto
    this.isSavingProject = false;
  }

  guardarComoProyecto() {
    if (!this.projectName || !this.projectName.trim()) {
      alert('Por favor, ingresa un nombre para el proyecto');
      return;
    }

    if (!this.canvas) {
      alert('Error: No hay contenido para guardar');
      return;
    }

    const userId = this.authService.getUserId();
    if (!userId) {
      alert('Error: No estás autenticado. Por favor, inicia sesión.');
      return;
    }

    this.isSavingProject = true;

    // Serializar elementos del canvas (igual que en ejecutarGuardado)
    const elements = this.canvas.getObjects().map((obj: any) => {
      const baseData: any = {
        type: obj.type,
        left: obj.left,
        top: obj.top,
        angle: obj.angle || 0,
        scaleX: obj.scaleX || 1,
        scaleY: obj.scaleY || 1
      };

      // Detectar si es un grupo con texto
      let textObj: any = obj;
      let backgroundRect: any = null;
      
      if (obj instanceof fabric.Group) {
        const objects = obj.getObjects();
        textObj = objects.find((o: any) => o instanceof fabric.Text);
        backgroundRect = objects.find((o: any) => o instanceof fabric.Rect);
      }
      
      if (textObj instanceof fabric.Text) {
        const textData: any = {
          ...baseData,
          type: 'text',
          text: textObj.text,
          fontSize: textObj.fontSize,
          fontFamily: textObj.fontFamily,
          fill: textObj.fill,
          fontWeight: textObj.fontWeight || 'normal',
          fontStyle: textObj.fontStyle || 'normal',
          underline: textObj.underline || false
        };
        
        // Si hay un rectángulo de fondo, usar sus propiedades
        if (backgroundRect) {
          const bgColor = backgroundRect.fill;
          if (bgColor && bgColor !== 'transparent' && bgColor !== '') {
            textData.backgroundColor = bgColor;
          }
          
          if (backgroundRect.strokeWidth && backgroundRect.strokeWidth > 0) {
            textData.stroke = backgroundRect.stroke;
            textData.strokeWidth = backgroundRect.strokeWidth;
          }
        }
        
        return textData;
      } else if (obj.type === 'image') {
        // Priorizar URL del storage - NUNCA usar base64 si hay URL del storage
        let imageSrc = '';
        
        // Primero intentar obtener storageUrl de las propiedades del objeto
        try {
          if (obj.get && typeof obj.get === 'function') {
            const storageUrl = obj.get('storageUrl');
            if (storageUrl && typeof storageUrl === 'string' && storageUrl.trim() !== '') {
              imageSrc = storageUrl;
            }
          }
        } catch (e) {
          console.warn('Error obteniendo storageUrl con get():', e);
        }
        
        // Si no se encontró con get(), intentar directamente desde la propiedad
        if (!imageSrc && obj.storageUrl && typeof obj.storageUrl === 'string' && obj.storageUrl.trim() !== '') {
          imageSrc = obj.storageUrl;
        }
        
        // Si aún no hay URL del storage, verificar si el src actual es una URL HTTP/HTTPS
        if (!imageSrc) {
          try {
            if (typeof obj.getSrc === 'function') {
              const src = obj.getSrc();
              // Si es una URL HTTP/HTTPS (del storage o pública), usarla directamente
              if (src && typeof src === 'string' && (src.startsWith('http://') || src.startsWith('https://'))) {
                imageSrc = src;
              }
            } else if (obj.src && typeof obj.src === 'string' && (obj.src.startsWith('http://') || obj.src.startsWith('https://'))) {
              imageSrc = obj.src;
            }
          } catch (e) {
            console.warn('Error obteniendo src de imagen:', e);
          }
        }
        
        // Si NO hay URL del storage, NO guardar base64 - dejar vacío o mostrar advertencia
        // Esto evita guardar imágenes grandes como base64
        if (!imageSrc) {
          console.warn('Advertencia: Imagen sin URL del storage. La imagen no se guardará correctamente.');
          // Opcional: podrías subir la imagen al storage aquí si no tiene URL
          // Por ahora, dejamos vacío para que el usuario sepa que hay un problema
          imageSrc = '';
        }
        
        return {
          ...baseData,
          type: 'image',
          src: imageSrc
        };
      } else if (obj.type === 'rect' || obj.type === 'circle' || obj.type === 'triangle' || obj.type === 'ellipse') {
        return {
          ...baseData,
          type: 'shape',
          shapeType: obj.type,
          width: obj.width,
          height: obj.height,
          fill: obj.fill,
          stroke: obj.stroke,
          strokeWidth: obj.strokeWidth
        };
      } else if (obj.type === 'line') {
        return {
          ...baseData,
          type: 'shape',
          shapeType: 'line',
          points: obj.points,
          stroke: obj.stroke,
          strokeWidth: obj.strokeWidth
        };
      }
      return null;
    }).filter((el: any) => el !== null);

    // Crear objeto de datos del proyecto
    const datosProyecto = {
      canvasWidth: this.canvasWidth,
      canvasHeight: this.canvasHeight,
      backgroundColor: this.backgroundColor,
      backgroundType: this.backgroundType,
      gradientColorStart: this.gradientColorStart,
      gradientColorEnd: this.gradientColorEnd,
      gradientDirection: this.gradientDirection,
      roundedCorners: this.roundedCorners,
      cornerRadius: this.cornerRadius,
      enableBorder: this.enableBorder,
      borderWidth: this.borderWidth,
      borderColor: this.borderColor,
      jpgQuality: this.jpgQuality,
      projectColor: this.projectColor, // Color del icono del proyecto
      elements: elements
    };

    // Generar miniatura
    const miniaturaSize = 200;
    const scale = Math.min(miniaturaSize / this.canvasWidth, miniaturaSize / this.canvasHeight);
    const thumbnailWidth = Math.floor(this.canvasWidth * scale);
    const thumbnailHeight = Math.floor(this.canvasHeight * scale);
    
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = thumbnailWidth;
    tempCanvas.height = thumbnailHeight;
    const tempCtx = tempCanvas.getContext('2d');
    
    let miniatura: string | undefined;
    try {
      if (tempCtx) {
        tempCtx.drawImage(
          this.canvas.getElement(),
          0, 0, this.canvasWidth, this.canvasHeight,
          0, 0, thumbnailWidth, thumbnailHeight
        );
        miniatura = tempCanvas.toDataURL('image/jpeg', 0.2);
      }
    } catch (e) {
      console.warn('No se pudo generar miniatura:', e);
    }

    // Crear el proyecto
    this.proyectosService.createProyecto({
      usuarioId: userId,
      nombre: this.projectName.trim(),
      tipo: 'banner',
      datosProyecto: datosProyecto,
      miniatura: miniatura
    }).subscribe({
      next: (proyecto) => {
        console.log('Proyecto guardado exitosamente:', proyecto);
        this.isSavingProject = false;
        this.closeSaveProjectModal();
        
        // Mostrar notificación de éxito (mismo estilo que guardar proyecto existente)
        this.mostrarNotificacionGuardado();
        this.markAsSaved();
        
        // Si el proyecto tiene ID, actualizar el proyectoId y recargar la lista
        if (proyecto && proyecto.Id) {
          this.proyectoId = proyecto.Id.toString();
          // Actualizar la URL con el nuevo proyectoId
          this.router.navigate(['/banner-editor'], { 
            queryParams: { proyectoId: this.proyectoId },
            replaceUrl: true 
          });
        }
      },
      error: (error) => {
        console.error('Error guardando proyecto:', error);
        this.isSavingProject = false;
        alert('Error al guardar el proyecto. Por favor intenta nuevamente.');
      }
    });
  }
}

